import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import * as XLSX from 'xlsx';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

ChartJS.register(ArcElement, Tooltip, Legend);

const BillingReports = () => {
    const [activeTab, setActiveTab] = useState('room-wise');
    const [loading, setLoading] = useState(false);

    // Room-wise bills state
    const [roomWiseBills, setRoomWiseBills] = useState([]);
    const [roomWiseSummary, setRoomWiseSummary] = useState({});
    const [roomFilters, setRoomFilters] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        roomNumber: '',
        bookingSource: ''
    });

    // OTA Revenue state
    const [otaRevenue, setOtaRevenue] = useState([]);
    const [otaSummary, setOtaSummary] = useState({});
    const [otaFilters, setOtaFilters] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    // GST Summary state
    const [gstData, setGstData] = useState({});
    const [gstFilters, setGstFilters] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    // Helper function to get auth headers with token
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    // Fetch Room-wise Bills
    const fetchRoomWiseBills = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (roomFilters.startDate) params.append('startDate', roomFilters.startDate);
            if (roomFilters.endDate) params.append('endDate', roomFilters.endDate);
            if (roomFilters.roomNumber) params.append('roomNumber', roomFilters.roomNumber);
            if (roomFilters.bookingSource) params.append('bookingSource', roomFilters.bookingSource);

            const response = await fetch(`${PRODUCTION_URL}/api/admin/billing/room-wise-bills?${params}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setRoomWiseBills(data.bills || []);
                setRoomWiseSummary(data.summary || {});
            } else {
                alert('Error fetching room-wise bills: ' + data.message);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error fetching room-wise bills');
        } finally {
            setLoading(false);
        }
    };

    // Fetch OTA Revenue
    const fetchOTARevenue = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (otaFilters.startDate) params.append('startDate', otaFilters.startDate);
            if (otaFilters.endDate) params.append('endDate', otaFilters.endDate);

            const response = await fetch(`${PRODUCTION_URL}/api/admin/billing/ota-revenue?${params}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setOtaRevenue(data.otaRevenue || []);
                setOtaSummary(data.summary || {});
            } else {
                alert('Error fetching OTA revenue: ' + data.message);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error fetching OTA revenue');
        } finally {
            setLoading(false);
        }
    };

    // Fetch GST Summary
    const fetchGSTSummary = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('startDate', gstFilters.startDate);
            params.append('endDate', gstFilters.endDate);

            const response = await fetch(`${PRODUCTION_URL}/api/admin/billing/gst-summary?${params}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setGstData(data);
            } else {
                alert('Error fetching GST summary: ' + data.message);
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error fetching GST summary');
        } finally {
            setLoading(false);
        }
    };

    // Export to Excel - Room-wise Bills
    const exportRoomWiseBills = () => {
        const ws_data = [
            ['Room-wise Bills Report'],
            [`Period: ${roomFilters.startDate} to ${roomFilters.endDate}`],
            [],
            ['Room No.', 'Guest Name', 'Check-in', 'Check-out', 'Nights', 'Source', 'Room Rent', 'Room GST', 'Other Charges', 'Other GST', 'Total GST', 'Grand Total', 'Paid', 'Balance'],
            ...roomWiseBills.map(bill => [
                bill.roomNumber,
                bill.guestName,
                new Date(bill.checkIn).toLocaleDateString(),
                new Date(bill.checkOut).toLocaleDateString(),
                bill.nights,
                bill.bookingSource,
                bill.roomRent.baseAmount,
                bill.roomRent.gstAmount,
                bill.otherChargesTotal.baseAmount,
                bill.otherChargesTotal.gstAmount,
                bill.grandTotal.gstAmount,
                bill.grandTotal.totalAmount,
                bill.totalPaid,
                bill.balance
            ]),
            [],
            ['Summary'],
            ['Total Bills:', roomWiseSummary.totalBills],
            ['Total Revenue:', roomWiseSummary.totalRevenue],
            ['Total GST:', roomWiseSummary.totalGST],
            ['Revenue without GST:', roomWiseSummary.totalRevenueWithoutGST]
        ];

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(wb, ws, 'Room-wise Bills');
        XLSX.writeFile(wb, `Room_Wise_Bills_${roomFilters.startDate}_to_${roomFilters.endDate}.xlsx`);
    };

    // Export to Excel - OTA Revenue
    const exportOTARevenue = () => {
        const ws_data = [
            ['OTA Revenue Report'],
            [`Period: ${otaFilters.startDate} to ${otaFilters.endDate}`],
            [],
            ['Booking Source', 'Total Bookings', 'Revenue', 'GST', 'Revenue without GST'],
            ...otaRevenue.map(ota => [
                ota.source,
                ota.bookings,
                ota.revenue,
                ota.gst,
                ota.revenueWithoutGST
            ]),
            [],
            ['Summary'],
            ['Total Bookings:', otaSummary.totalBookings],
            ['Total Revenue:', otaSummary.totalRevenue],
            ['Total GST:', otaSummary.totalGST],
            ['Revenue without GST:', otaSummary.totalRevenueWithoutGST]
        ];

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(wb, ws, 'OTA Revenue');
        XLSX.writeFile(wb, `OTA_Revenue_${otaFilters.startDate}_to_${otaFilters.endDate}.xlsx`);
    };

    // Export to Excel - GST Summary
    const exportGSTSummary = () => {
        const ws_data = [
            ['GST Summary Report'],
            [`Period: ${gstFilters.startDate} to ${gstFilters.endDate}`],
            [],
            ['GST Breakdown'],
            ['Room Rent GST:', gstData.gstBreakdown?.roomRentGST || 0],
            ['Other Charges GST:', gstData.gstBreakdown?.otherChargesGST || 0],
            ['Total GST:', gstData.gstBreakdown?.totalGST || 0],
            [],
            ['Revenue Summary'],
            ['Room Rent (excl. GST):', gstData.revenue?.roomRent || 0],
            ['Other Charges (excl. GST):', gstData.revenue?.otherCharges || 0],
            ['Total without GST:', gstData.revenue?.totalWithoutGST || 0],
            ['Total with GST:', gstData.revenue?.totalWithGST || 0]
        ];

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(wb, ws, 'GST Summary');
        XLSX.writeFile(wb, `GST_Summary_${gstFilters.startDate}_to_${gstFilters.endDate}.xlsx`);
    };

    // Load data based on active tab
    useEffect(() => {
        if (activeTab === 'room-wise') {
            fetchRoomWiseBills();
        } else if (activeTab === 'ota-revenue') {
            fetchOTARevenue();
        } else if (activeTab === 'gst-summary') {
            fetchGSTSummary();
        }
    }, [activeTab]);

    // Chart data for OTA Revenue
    const otaChartData = {
        labels: otaRevenue.map(ota => ota.source),
        datasets: [{
            data: otaRevenue.map(ota => ota.revenue),
            backgroundColor: [
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
                '#4BC0C0',
                '#9966FF',
                '#FF9F40',
                '#FF6384',
                '#C9CBCF'
            ],
            hoverOffset: 4
        }]
    };

    return (
        <AdminLayout>
            <div className="billing-reports-container">
                <style>{`
                    .billing-reports-container {
                        padding: 20px;
                        background: #f5f5f5;
                        min-height: 100vh;
                    }

                    .page-header {
                        margin-bottom: 30px;
                    }

                    .page-header h1 {
                        font-size: 28px;
                        font-weight: 700;
                        color: #1a1a1a;
                        margin: 0 0 10px 0;
                    }

                    .page-header p {
                        color: #666;
                        margin: 0;
                    }

                    .tabs-container {
                        background: white;
                        border-radius: 10px;
                        padding: 0;
                        margin-bottom: 20px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                        overflow: hidden;
                    }

                    .tabs {
                        display: flex;
                        border-bottom: 2px solid #e0e0e0;
                    }

                    .tab {
                        flex: 1;
                        padding: 15px 20px;
                        border: none;
                        background: none;
                        cursor: pointer;
                        font-size: 16px;
                        font-weight: 500;
                        color: #666;
                        transition: all 0.3s;
                        border-bottom: 3px solid transparent;
                    }

                    .tab:hover {
                        background: #f5f5f5;
                    }

                    .tab.active {
                        color: #2563eb;
                        border-bottom-color: #2563eb;
                        background: #eff6ff;
                    }

                    .tab-content {
                        padding: 30px;
                        background: white;
                        border-radius: 0 0 10px 10px;
                    }

                    .filters-section {
                        background: #f9fafb;
                        padding: 20px;
                        border-radius: 8px;
                        margin-bottom: 20px;
                        display: flex;
                        gap: 15px;
                        flex-wrap: wrap;
                        align-items: end;
                    }

                    .filter-group {
                        flex: 1;
                        min-width: 200px;
                    }

                    .filter-group label {
                        display: block;
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        margin-bottom: 8px;
                    }

                    .filter-group input,
                    .filter-group select {
                        width: 100%;
                        padding: 10px 15px;
                        border: 1px solid #d1d5db;
                        border-radius: 6px;
                        font-size: 14px;
                        transition: all 0.3s;
                    }

                    .filter-group input:focus,
                    .filter-group select:focus {
                        outline: none;
                        border-color: #2563eb;
                        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                    }

                    .btn {
                        padding: 10px 20px;
                        border: none;
                        border-radius: 6px;
                        font-size: 14px;
                        font-weight: 500;
                        cursor: pointer;
                        transition: all 0.3s;
                    }

                    .btn-primary {
                        background: #2563eb;
                        color: white;
                    }

                    .btn-primary:hover {
                        background: #1d4ed8;
                    }

                    .btn-success {
                        background: #10b981;
                        color: white;
                    }

                    .btn-success:hover {
                        background: #059669;
                    }

                    .summary-cards {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 20px;
                        margin-bottom: 30px;
                    }

                    .summary-card {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        padding: 25px;
                        border-radius: 10px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                    }

                    .summary-card.green {
                        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    }

                    .summary-card.blue {
                        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    }

                    .summary-card.purple {
                        background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
                    }

                    .summary-card h3 {
                        font-size: 14px;
                        font-weight: 500;
                        margin: 0 0 10px 0;
                        opacity: 0.9;
                    }

                    .summary-card .amount {
                        font-size: 28px;
                        font-weight: 700;
                        margin: 0;
                    }

                    .data-table {
                        width: 100%;
                        border-collapse: collapse;
                        background: white;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    }

                    .data-table thead {
                        background: #f9fafb;
                    }

                    .data-table th {
                        padding: 15px;
                        text-align: left;
                        font-size: 13px;
                        font-weight: 600;
                        color: #374151;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e5e7eb;
                    }

                    .data-table td {
                        padding: 15px;
                        border-bottom: 1px solid #f3f4f6;
                        color: #1f2937;
                        font-size: 14px;
                    }

                    .data-table tbody tr:hover {
                        background: #f9fafb;
                    }

                    .data-table tbody tr:last-child td {
                        border-bottom: none;
                    }

                    .badge {
                        display: inline-block;
                        padding: 4px 12px;
                        border-radius: 12px;
                        font-size: 12px;
                        font-weight: 500;
                    }

                    .badge-success {
                        background: #d1fae5;
                        color: #065f46;
                    }

                    .badge-warning {
                        background: #fef3c7;
                        color: #92400e;
                    }

                    .badge-danger {
                        background: #fee2e2;
                        color: #991b1b;
                    }

                    .loading {
                        text-align: center;
                        padding: 60px 20px;
                        color: #666;
                    }

                    .loading-spinner {
                        border: 4px solid #f3f4f6;
                        border-top: 4px solid #2563eb;
                        border-radius: 50%;
                        width: 50px;
                        height: 50px;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 20px;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .chart-container {
                        max-width: 500px;
                        margin: 0 auto 30px;
                    }

                    .no-data {
                        text-align: center;
                        padding: 60px 20px;
                        color: #9ca3af;
                    }

                    .no-data svg {
                        width: 80px;
                        height: 80px;
                        margin-bottom: 20px;
                        opacity: 0.5;
                    }

                    @media (max-width: 768px) {
                        .billing-reports-container {
                            padding: 10px;
                        }

                        .tabs {
                            flex-direction: column;
                        }

                        .tab {
                            border-bottom: 1px solid #e0e0e0;
                            border-right: none;
                        }

                        .filters-section {
                            flex-direction: column;
                        }

                        .filter-group {
                            width: 100%;
                        }

                        .data-table {
                            font-size: 12px;
                        }

                        .data-table th,
                        .data-table td {
                            padding: 10px;
                        }
                    }
                `}</style>

                <div className="page-header">
                    <h1>📊 Billing & Reports</h1>
                    <p>Comprehensive billing reports with GST breakdown and OTA revenue analysis</p>
                </div>

                <div className="tabs-container">
                    <div className="tabs">
                        <button
                            className={`tab ${activeTab === 'room-wise' ? 'active' : ''}`}
                            onClick={() => setActiveTab('room-wise')}
                        >
                            Room-wise Bills
                        </button>
                        <button
                            className={`tab ${activeTab === 'ota-revenue' ? 'active' : ''}`}
                            onClick={() => setActiveTab('ota-revenue')}
                        >
                            OTA Revenue
                        </button>
                        <button
                            className={`tab ${activeTab === 'gst-summary' ? 'active' : ''}`}
                            onClick={() => setActiveTab('gst-summary')}
                        >
                            GST Summary
                        </button>
                    </div>

                    <div className="tab-content">
                        {loading ? (
                            <div className="loading">
                                <div className="loading-spinner"></div>
                                <p>Loading data...</p>
                            </div>
                        ) : (
                            <>
                                {/* ROOM-WISE BILLS TAB */}
                                {activeTab === 'room-wise' && (
                                    <>
                                        <div className="filters-section">
                                            <div className="filter-group">
                                                <label>Start Date</label>
                                                <input
                                                    type="date"
                                                    value={roomFilters.startDate}
                                                    onChange={(e) => setRoomFilters({ ...roomFilters, startDate: e.target.value })}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label>End Date</label>
                                                <input
                                                    type="date"
                                                    value={roomFilters.endDate}
                                                    onChange={(e) => setRoomFilters({ ...roomFilters, endDate: e.target.value })}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label>Room Number</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g., 101"
                                                    value={roomFilters.roomNumber}
                                                    onChange={(e) => setRoomFilters({ ...roomFilters, roomNumber: e.target.value })}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label>Booking Source</label>
                                                <select
                                                    value={roomFilters.bookingSource}
                                                    onChange={(e) => setRoomFilters({ ...roomFilters, bookingSource: e.target.value })}
                                                >
                                                    <option value="">All Sources</option>
                                                    <option value="Direct">Direct</option>
                                                    <option value="MakeMyTrip">MakeMyTrip</option>
                                                    <option value="Goibibo">Goibibo</option>
                                                    <option value="Booking.com">Booking.com</option>
                                                    <option value="Agoda">Agoda</option>
                                                    <option value="OYO">OYO</option>
                                                    <option value="Airbnb">Airbnb</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            <button className="btn btn-primary" onClick={fetchRoomWiseBills}>
                                                🔍 Search
                                            </button>
                                            <button className="btn btn-success" onClick={exportRoomWiseBills}>
                                                📥 Export Excel
                                            </button>
                                        </div>

                                        {/* Summary Cards */}
                                        <div className="summary-cards">
                                            <div className="summary-card blue">
                                                <h3>Total Bills</h3>
                                                <p className="amount">{roomWiseSummary.totalBills || 0}</p>
                                            </div>
                                            <div className="summary-card green">
                                                <h3>Total Revenue</h3>
                                                <p className="amount">₹{(roomWiseSummary.totalRevenue || 0).toLocaleString()}</p>
                                            </div>
                                            <div className="summary-card purple">
                                                <h3>Total GST</h3>
                                                <p className="amount">₹{(roomWiseSummary.totalGST || 0).toLocaleString()}</p>
                                            </div>
                                            <div className="summary-card">
                                                <h3>Revenue (excl. GST)</h3>
                                                <p className="amount">₹{(roomWiseSummary.totalRevenueWithoutGST || 0).toLocaleString()}</p>
                                            </div>
                                        </div>

                                        {/* Table */}
                                        {roomWiseBills.length > 0 ? (
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Room</th>
                                                            <th>Guest</th>
                                                            <th>Check-in → Out</th>
                                                            <th>Nights</th>
                                                            <th>Source</th>
                                                            <th>Room Rent</th>
                                                            <th>Room GST</th>
                                                            <th>Other</th>
                                                            <th>Other GST</th>
                                                            <th>Total GST</th>
                                                            <th>Grand Total</th>
                                                            <th>Paid</th>
                                                            <th>Balance</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {roomWiseBills.map(bill => (
                                                            <tr key={bill.bookingId}>
                                                                <td><strong>{bill.roomNumber}</strong><br /><small>{bill.roomType}</small></td>
                                                                <td>{bill.guestName}</td>
                                                                <td>
                                                                    <small>
                                                                        {new Date(bill.checkIn).toLocaleDateString()}<br />
                                                                        → {new Date(bill.checkOut).toLocaleDateString()}
                                                                    </small>
                                                                </td>
                                                                <td>{bill.nights}</td>
                                                                <td><span className="badge badge-success">{bill.bookingSource}</span></td>
                                                                <td>₹{bill.roomRent.baseAmount}</td>
                                                                <td>₹{bill.roomRent.gstAmount}</td>
                                                                <td>₹{bill.otherChargesTotal.baseAmount}</td>
                                                                <td>₹{bill.otherChargesTotal.gstAmount}</td>
                                                                <td><strong>₹{bill.grandTotal.gstAmount}</strong></td>
                                                                <td><strong>₹{bill.grandTotal.totalAmount}</strong></td>
                                                                <td>₹{bill.totalPaid}</td>
                                                                <td>
                                                                    {bill.balance > 0 ? (
                                                                        <span className="badge badge-danger">₹{bill.balance}</span>
                                                                    ) : (
                                                                        <span className="badge badge-success">Paid</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="no-data">
                                                <p>No billing data found for the selected filters</p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* OTA REVENUE TAB */}
                                {activeTab === 'ota-revenue' && (
                                    <>
                                        <div className="filters-section">
                                            <div className="filter-group">
                                                <label>Start Date</label>
                                                <input
                                                    type="date"
                                                    value={otaFilters.startDate}
                                                    onChange={(e) => setOtaFilters({ ...otaFilters, startDate: e.target.value })}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label>End Date</label>
                                                <input
                                                    type="date"
                                                    value={otaFilters.endDate}
                                                    onChange={(e) => setOtaFilters({ ...otaFilters, endDate: e.target.value })}
                                                />
                                            </div>
                                            <button className="btn btn-primary" onClick={fetchOTARevenue}>
                                                🔍 Search
                                            </button>
                                            <button className="btn btn-success" onClick={exportOTARevenue}>
                                                📥 Export Excel
                                            </button>
                                        </div>

                                        {/* Summary Cards */}
                                        <div className="summary-cards">
                                            <div className="summary-card blue">
                                                <h3>Total Bookings</h3>
                                                <p className="amount">{otaSummary.totalBookings || 0}</p>
                                            </div>
                                            <div className="summary-card green">
                                                <h3>Total Revenue</h3>
                                                <p className="amount">₹{(otaSummary.totalRevenue || 0).toLocaleString()}</p>
                                            </div>
                                            <div className="summary-card purple">
                                                <h3>Total GST</h3>
                                                <p className="amount">₹{(otaSummary.totalGST || 0).toLocaleString()}</p>
                                            </div>
                                            <div className="summary-card">
                                                <h3>Revenue (excl. GST)</h3>
                                                <p className="amount">₹{(otaSummary.totalRevenueWithoutGST || 0).toLocaleString()}</p>
                                            </div>
                                        </div>

                                        {/* Chart */}
                                        {otaRevenue.length > 0 && (
                                            <div className="chart-container">
                                                <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Revenue by Source</h3>
                                                <Pie data={otaChartData} />
                                            </div>
                                        )}

                                        {/* Table */}
                                        {otaRevenue.length > 0 ? (
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th>Booking Source</th>
                                                        <th>Bookings</th>
                                                        <th>Revenue</th>
                                                        <th>GST</th>
                                                        <th>Revenue (excl. GST)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {otaRevenue.map((ota, idx) => (
                                                        <tr key={idx}>
                                                            <td><strong>{ota.source}</strong></td>
                                                            <td>{ota.bookings}</td>
                                                            <td>₹{ota.revenue.toLocaleString()}</td>
                                                            <td>₹{ota.gst.toLocaleString()}</td>
                                                            <td>₹{ota.revenueWithoutGST.toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="no-data">
                                                <p>No OTA revenue data found for the selected period</p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* GST SUMMARY TAB */}
                                {activeTab === 'gst-summary' && (
                                    <>
                                        <div className="filters-section">
                                            <div className="filter-group">
                                                <label>Start Date *</label>
                                                <input
                                                    type="date"
                                                    value={gstFilters.startDate}
                                                    onChange={(e) => setGstFilters({ ...gstFilters, startDate: e.target.value })}
                                                />
                                            </div>
                                            <div className="filter-group">
                                                <label>End Date *</label>
                                                <input
                                                    type="date"
                                                    value={gstFilters.endDate}
                                                    onChange={(e) => setGstFilters({ ...gstFilters, endDate: e.target.value })}
                                                />
                                            </div>
                                            <button className="btn btn-primary" onClick={fetchGSTSummary}>
                                                🔍 Generate Report
                                            </button>
                                            <button className="btn btn-success" onClick={exportGSTSummary}>
                                                📥 Export Excel
                                            </button>
                                        </div>

                                        {gstData.gstBreakdown && (
                                            <>
                                                {/* Summary Cards */}
                                                <div className="summary-cards">
                                                    <div className="summary-card">
                                                        <h3>Revenue (excl. GST)</h3>
                                                        <p className="amount">₹{(gstData.revenue.totalWithoutGST || 0).toLocaleString()}</p>
                                                    </div>
                                                    <div className="summary-card purple">
                                                        <h3>Total GST Collected</h3>
                                                        <p className="amount">₹{(gstData.gstBreakdown.totalGST || 0).toLocaleString()}</p>
                                                    </div>
                                                    <div className="summary-card green">
                                                        <h3>Total Revenue (incl. GST)</h3>
                                                        <p className="amount">₹{(gstData.revenue.totalWithGST || 0).toLocaleString()}</p>
                                                    </div>
                                                </div>

                                                {/* Breakdown Table */}
                                                <h3 style={{ marginBottom: '20px' }}>GST Breakdown</h3>
                                                <table className="data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Category</th>
                                                            <th>Base Amount</th>
                                                            <th>GST Amount</th>
                                                            <th>GST Rate</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>Room Rent</strong></td>
                                                            <td>₹{(gstData.revenue.roomRent || 0).toLocaleString()}</td>
                                                            <td>₹{(gstData.gstBreakdown.roomRentGST || 0).toLocaleString()}</td>
                                                            <td><span className="badge badge-success">12%</span></td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Other Charges</strong></td>
                                                            <td>₹{(gstData.revenue.otherCharges || 0).toLocaleString()}</td>
                                                            <td>₹{(gstData.gstBreakdown.otherChargesGST || 0).toLocaleString()}</td>
                                                            <td><span className="badge badge-warning">18%</span></td>
                                                        </tr>
                                                        <tr style={{ background: '#f9fafb', fontWeight: 'bold' }}>
                                                            <td><strong>Total</strong></td>
                                                            <td>₹{(gstData.revenue.totalWithoutGST || 0).toLocaleString()}</td>
                                                            <td>₹{(gstData.gstBreakdown.totalGST || 0).toLocaleString()}</td>
                                                            <td>-</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </>
                                        )}

                                        {!gstData.gstBreakdown && (
                                            <div className="no-data">
                                                <p>Select a date range and click "Generate Report" to view GST summary</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default BillingReports;
